<template>
  <ts-card style="height: inherit;" class="ts-flex ts-flex-col">
    <div class="form-header ts-bg-gray-100 ts-border-b ts-border-gray-400 ts-p-4 ts-relative">
      <h4 class="ts-text-sm">{{ $t('user.pageTitle') }}</h4>
      <ts-modal-close-button />
    </div>
    <ts-card-wrapper class="ts-flex-grow ts-overflow-hidden">
      <form class="ts-overflow-hidden ts-h-full ts-flex ts-flex-col">
        <div class="ts-flex ts--mx-2">
          <div class="ts-w-1/3 ts-px-2">
            <div class="row">
              <div class="col-sm-12">
                <div class="form-group m-b-10">
                  <label class="col-form-label control-label">{{ $t('user.userName') }}</label>
                  <div>
                    <ts-input
                      v-model="model.user_name"
                      :class="{ 'is-invalid': errors.has('user_name') }"
                      autofocus
                      v-if="this.editData === undefined"
                    />
                    <ts-input :value="model.user_name" disabled v-else />
                    <div class="invalid-feedback" v-if="errors.has('user_name')">{{ errors.first('user_name') }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="ts-w-1/3 ts-px-2">
            <div class="row">
              <div class="col-sm-12">
                <div class="form-group m-b-10">
                  <label class="col-form-label control-label">{{ $t('user.fullName') }}</label>
                  <div>
                    <ts-input v-model="model.full_name" :class="{ 'is-invalid': errors.has('full_name') }" />
                    <div class="invalid-feedback" v-if="errors.has('full_name')">{{ errors.first('full_name') }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="ts-w-1/3 ts-px-2">
            <div class="row">
              <div class="col-sm-12">
                <div class="form-group m-b-10">
                  <label class="col-form-label control-label">{{ $t('user.companyName') }}</label>
                  <div>
                    <ts-loading-banner :loading="loadingCompany">
                      <ts-select
                        v-model="model.company_id"
                        :options="companies.map(c => ({ text: c.company_name_en, value: c.company_id }))"
                        :class="{ 'is-invalid': errors.has('company_id') }"
                      />
                    </ts-loading-banner>
                    <div class="invalid-feedback" v-if="errors.has('company_id')">{{ errors.first('company_id') }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="ts-flex ts--mx-2">
          <div class="ts-w-1/3 ts-px-2">
            <div class="row" v-if="this.editData === undefined">
              <div class="col-sm-12">
                <div class="form-group m-b-10">
                  <label class="col-form-label control-label">{{ $t('user.password') }}</label>
                  <div>
                    <ts-input
                      type="password"
                      v-model="model.password"
                      :class="{ 'is-invalid': errors.has('password') }"
                    />
                    <div class="invalid-feedback" v-if="errors.has('password')">{{ errors.first('password') }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="ts-w-1/3 ts-px-2">
            <div class="row" v-if="this.editData === undefined">
              <div class="col-sm-12">
                <div class="form-group m-b-10">
                  <label class="col-form-label control-label">{{ $t('user.passwordConfirmation') }}</label>
                  <div>
                    <ts-input
                      type="password"
                      v-model="model.password_confirmation"
                      :class="{ 'is-invalid': errors.has('password_confirmation') }"
                    />
                    <div class="invalid-feedback" v-if="errors.has('password_confirmation')">
                      {{ errors.first('password_confirmation') }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="ts-w-1/3 ts-px-2"></div>
        </div>
        <div class="row">
          <div class="col-sm-12">
            <div class="form-group m-b-10">
              <div>
                <ts-checkbox v-model="model.disable">Disable</ts-checkbox>
              </div>
            </div>
          </div>
        </div>
        <div class="ts-flex ts-items-stretch ts--mx-2">
          <div class="ts-w-1/2 ts-px-2">
            <div class="row ts-h-full">
              <div class="col-sm-12">
                <div class="form-group ts-h-full ts-flex ts-flex-col">
                  <label class="col-form-label control-label">{{ $t('user.role') }}</label>
                  <ts-loading-banner :loading="loadingRole">
                    <ul class="ts-reset ts-px-4 ts-py-2 ts-border ts-border-gray-300 ts-rounded ts-h-full">
                      <li v-for="role in roles" :key="role.id">
                        <ts-checkbox v-model="model.roles" :value="role.id">{{ role.name }}</ts-checkbox>
                      </li>
                    </ul>
                  </ts-loading-banner>
                </div>
              </div>
            </div>
          </div>
          <div class="ts-w-1/2 ts-px-2">
            <div class="row ts-h-full">
              <div class="col-sm-12">
                <div class="form-group ts-h-full ts-flex ts-flex-col">
                  <label class="col-form-label control-label">{{ $t('user.branch') }}</label>
                  <ts-loading-banner :loading="loadingBranch">
                    <ul class="ts-reset ts-px-4 ts-py-2 ts-border ts-border-gray-300 ts-rounded ts-h-full">
                      <li v-for="branch in branches" :key="branch.branch_id">
                        <div class="ts-flex ts-justify-between ts-items-center">
                          <ts-checkbox
                            :checkedValue="Object.keys(model.branches)"
                            @change="setBranches"
                            :value="`${branch.branch_id}`"
                            >{{ branch.branch_name_en }}</ts-checkbox
                          >
                          <div
                            class="custom-control custom-switch"
                            style="padding-top: 3px;"
                            v-if="Object.keys(model.branches).includes(`${branch.branch_id}`)"
                          >
                            <input
                              type="checkbox"
                              class="custom-control-input"
                              v-model="model.branches[branch.branch_id].is_default"
                              @input="event => setAsDefaultBranch(branch.branch_id, event)"
                              :id="`customSwitchBranch-${branch.branch_id}`"
                            />
                            <label
                              class="custom-control-label"
                              :for="`customSwitchBranch-${branch.branch_id}`"
                              v-tooltip="'Set default branch'"
                            ></label>
                          </div>
                        </div>
                      </li>
                    </ul>
                  </ts-loading-banner>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="row ts-mt-6">
          <div class="col-sm-12 col-lg-12 col-md-12 text-right">
            <ts-button @click.prevent="$emit('close')" :disabled="saving">{{ $t('button.cancel') }}</ts-button>
            <ts-button
              outline
              color="primary"
              class="ml-2"
              :waiting="savingAndNew"
              @click.prevent="saveAndNew"
              v-if="editData === undefined"
              >{{ $t('button.createAndNew') }}</ts-button
            >
            <ts-button color="primary" class="ml-2" :waiting="saving" @click.prevent="save">
              {{ editData ? $t('button.update') : $t('button.create') }}
            </ts-button>
          </div>
        </div>
      </form>
    </ts-card-wrapper>
  </ts-card>
</template>

<script>
import { Errors } from 'form-backend-validation'
import { cloneDeep, transform } from 'lodash'
import { mapState } from 'vuex'

export default {
  name: 'user-form',
  props: ['editData'],
  data() {
    const model = {
      user_name: this.editData ? this.editData.user_name : '',
      full_name: this.editData ? this.editData.full_name : '',
      password: '',
      password_confirmation: '',
      company_id: this.editData ? this.editData.company_id : '',
      disable: this.editData ? this.editData.disable : false,
      roles: this.editData ? this.editData.roles.map(r => r.id) : [],
      branches: this.editData
        ? transform(
            this.editData.branches,
            function(result, branch) {
              result[branch.branch_id] = { is_default: branch.pivot.is_default }
            },
            {}
          )
        : {}
    }

    return {
      newModel: model,
      model: cloneDeep(model),
      errors: new Errors(),
      saving: false,
      savingAndNew: false,
      loadingRole: false,
      loadingCompany: false,
      loadingBranch: false
    }
  },
  computed: {
    ...mapState('security/user', ['roles', 'companies', 'branches'])
  },
  created() {
    this.fetchRoles()
    this.fetchCompanies()
    this.fetchBranches()
  },
  methods: {
    setAsDefaultBranch(branchId, event) {
      Object.keys(this.model.branches)
        .filter(bKey => bKey != branchId)
        .forEach(bKey => {
          this.$set(this.model.branches[bKey], 'is_default', false)
        })

      this.$set(this.model.branches[branchId], 'is_default', event.target.checked)
      this.$forceUpdate()
    },
    setBranches(branches) {
      this.model.branches = transform(
        branches,
        (result, branchId) => {
          result[branchId] = {
            is_default: this.model.branches[branchId] ? this.model.branches[branchId].is_default : false
          }
        },
        {}
      )
    },
    fetchRoles() {
      this.loadingRole = true

      this.$store.dispatch('security/user/fetchRoles').finally(() => (this.loadingRole = false))
    },
    fetchCompanies() {
      this.loadingCompany = true

      this.$store.dispatch('security/user/fetchCompanies').finally(() => (this.loadingCompany = false))
    },
    fetchBranches() {
      this.loadingBranch = true

      this.$store.dispatch('security/user/fetchBranches').finally(() => (this.loadingBranch = false))
    },
    submit() {
      let responseResult

      this.errors = new Errors()

      if (this.editData) {
        responseResult = this.$store.dispatch('security/user/update', {
          id: this.editData.user_id,
          ...this.model
        })
      } else {
        responseResult = this.$store.dispatch('security/user/create', this.model)
      }

      return responseResult
        .then(response => {
          return this.$store.dispatch('security/user/fetch').then(res => {
            this.saving = false
            this.$toasted.success(response.message)
            return Promise.resolve(res)
          })
        })
        .catch(error => {
          this.errors = new Errors(error.errors)
          this.$toasted.error(error.message)

          return Promise.reject(error)
        })
    },
    save() {
      this.saving = true

      this.submit()
        .then(() => {
          this.$emit('close')
        })
        .finally(() => {
          this.saving = false
        })
    },
    saveAndNew() {
      this.savingAndNew = true

      this.submit()
        .then(() => {
          this.model = cloneDeep(this.newModel)
        })
        .finally(() => {
          this.savingAndNew = false
        })
    }
  }
}
</script>
